/* eslint-disable no-param-reassign */
import { ReactElement, useCallback, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { GoBackButton, Button, ConfirmModal } from '~/components';
import { useLoading } from '~/hooks/loading';
import { IExerciseRequiredForm } from '~/models/Exercise';
import { ExerciseService } from '~/services';
import { handleYupValidationError } from '~/utils';
import { ExerciseSchema } from '~/utils/schemas/ExerciseSchema';

import EditExerciseForm from '../EditExerciseForm';

import * as S from './styles';

interface EditExerciseParams {
  exerciseId: string;
}

function EditExercise(): ReactElement {
  const { setLoading } = useLoading();

  const formRef = useRef<FormHandles>(null);
  const { exerciseId } = useParams<EditExerciseParams>();
  const router = useHistory();
  const [confirmDeleteModal, setConfirmDeleteModal] = useState<boolean>(false);

  const handleCancel = useCallback(() => {
    router.goBack();
  }, [router]);

  const handleEditExerciseSubmit = useCallback(
    async (data: IExerciseRequiredForm) => {
      try {
        setLoading(true);

        formRef.current?.setErrors({});

        if (!data.video_url) {
          data.video_url = '';
        }

        if (!data.video_description) {
          data.video_description = '';
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        await ExerciseSchema.validate(data, {
          abortEarly: false,
        });

        const result = await ExerciseService.editExercise(exerciseId, data);
        if (result.status === 200) {
          toast.success('Exercício editado com sucesso!');
          router.goBack();
        } else {
          toast.error('Ocorreu um problema ao editar o exercício!');
        }

        setLoading(false);
      } catch (error) {
        setLoading(false);
        handleYupValidationError(formRef, error);
      }
    },
    [exerciseId, router, setLoading],
  );

  const handleDeleteExercise = async () => {
    try {
      setLoading(true);

      const result = await ExerciseService.deleteExercise(exerciseId);
      if (result.status === 200) {
        toast.success('Exercicio deletado com sucesso!');
        router.goBack();
      } else {
        toast.error('Ocorreu um problema ao deletar o exercicio!');
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error('Ocorreu um problema ao deletar o exercicio!');
    }
  };

  return (
    <>
      <GoBackButton
        containerStyle={{ alignSelf: 'flex-start', paddingTop: '2.2rem' }}
      />
      <S.Container>
        <S.Header>
          <S.Title>Editar Exercício</S.Title>
        </S.Header>
        <EditExerciseForm
          formRef={formRef}
          formName="edit-method"
          handleSubmit={handleEditExerciseSubmit}
          exerciseId={exerciseId}
        />
        <S.Footer>
          <Button
            form="edit-method"
            type="submit"
            textContent="Salvar"
            variant="black"
            size="small"
            extraStyles={{
              width: '200px',
            }}
          />
          <Button
            type="button"
            onClick={() => setConfirmDeleteModal(true)}
            textContent="Excluir"
            variant="red"
            size="small"
            extraStyles={{
              width: '200px',
            }}
          />
        </S.Footer>
      </S.Container>
      <ConfirmModal
        modalIsVisible={confirmDeleteModal}
        setModalIsVisible={setConfirmDeleteModal}
        handleSubmit={handleDeleteExercise}
        textContent="Tem certeza de que deseja excluir o exercício?"
        title="Deletar exercício"
      />
    </>
  );
}

export default EditExercise;
